// §II · STAGES (the recorded arc) — ported VERBATIM from the Claude Design canon
// `R10 - §II - canon F1.html` (project dd7b95d4, data-canon="R10-§II-F1"). Layout =
// R9 reference, restored (styles/r10-ii.css). Only the spec-forced additions live
// here: pagination (measured budget, s368.d2), the 22 fields' degradation/empty
// states, content-fit caps (R10 - caps.md), and the §II terminology — §II never says
// "carrier"; that term is reserved for §IV. §II says "the red line runs through …".
//
// §II is MULTI-SHEET: unlike §I/§III it renders its OWN artboard shells (one <section>
// per sheet) with the §II runner + "N of N" footer, so it is mounted WITHOUT the
// generic Artboard wrapper.
//
// Data is board-driven (a `view`), exactly like the canon standalone: stage names,
// dates, step-3 rating and BG1–BG4 come from the wizard side (Track B, not built yet),
// and `carriers` (which stages the red line runs through) is a §IV-derived stage-index
// list the canon carries in the board. `means` is the real per-stage mean of the seven
// sliders (= `passport.silhouette`). The engine→view adapter lands with the wizard.

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { STAGE_NAME_MAX, LIVING_FOR_MAX } from '../../contracts.js';
import { filled } from '../../insights/input.js';
import { planSheets, measureSectionII } from '../docFit.js';

// useLayoutEffect warns under SSR (it can't run there) — fall back to useEffect on the
// server. The measured-pagination pass is a client-only concern; on the server the
// declared board plan renders, which is exactly the parity contract.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const NUM_WORD = {
  2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight',
  9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
};
const ROMAN = (n) => ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV'][n];

// caps re-measured against the R9 card slots (see "R10 - caps.md")
// CAP_NAME now comes from the contract (cc-31): step 2 has to show the same limit at typing time,
// and two copies of "80" in two layers is exactly how the wizard and the render drift apart.
// The cut itself stays here — it is the guard for producers that never saw an input field.
const CAP_NAME = STAGE_NAME_MAX;
// CAP_LIVING тоже приехал из контракта (решение Кирилла 21.08). До этого 64 стояло здесь
// литералом, и разница с соседней строкой считалась содержательной: будто 80 — валидация
// ввода, а 64 — обрезка печати. Замер показал, что обе молчат одинаково и стоят в одной
// функции; отличались они только МЕСТОМ ХРАНЕНИЯ. Теперь оба числа живут там, где их видят
// обе стороны шва.
const CAP_LIVING = LIVING_FOR_MAX;
// провизорно (F1): разделитель мульти-выбора BG1. Один символ, один слот.
const BG1_SEP = ' · ';

// word-boundary truncation + ellipsis; { t, cut }
function cap(s, n) {
  if (!s || s.length <= n) return { t: s, cut: false };
  let c = s.slice(0, n);
  const sp = c.lastIndexOf(' ');
  if (sp > n * 0.6) c = c.slice(0, sp);
  return { t: c + '…', cut: true };
}

// ---- R9 card curve: whole arc, this stage highlit (verbatim `cardCurve`) ----
function curvePath(means) {
  const w = 220, h = 56, N = means.length;
  const xs = means.map((_, i) => (N === 1 ? w / 2 : 8 + (i / (N - 1)) * (w - 16)));
  const ys = means.map((v) => h / 2 - (v / 3) * (h / 2 - 8));
  const pts = xs.map((x, i) => ({ x, y: ys[i] }));
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || pts[i + 1];
    d += ` C ${(p1.x + (p2.x - p0.x) / 6).toFixed(1)} ${(p1.y + (p2.y - p0.y) / 6).toFixed(1)}, ${(p2.x - (p3.x - p1.x) / 6).toFixed(1)} ${(p2.y - (p3.y - p1.y) / 6).toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return { d, pts, w, h };
}

function CardCurve({ means, idx }) {
  const { d, pts, w, h } = curvePath(means);
  return (
    <svg className="st-curve" data-field="stages[i].mini_curve" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <line x1="0" y1={h / 2} x2={w} y2={h / 2} stroke="rgba(143,136,122,.18)" strokeDasharray="2 4" strokeWidth=".5" />
      <path d={d} fill="none" stroke="#D48A3C" strokeWidth="1.2" opacity=".55" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={i === idx ? 3.4 : 1.6} fill={i === idx ? '#C8482B' : '#5A554A'} />
      ))}
    </svg>
  );
}

// ---- rating_bar · CANON MAPPING (F3, s836.d2) ----------------------------------
//   The source is no longer a wizard field: the step-3 overall rating was cut (lock
//   06.08 — not in contracts.js). The bar shows the MEAN OF TOUCHED LAYERS of the
//   stage — the SAME number the mini-curve under it plots (view.means[i]). An
//   untouched layer is not zero and is not in the mean (silhouette.js, s446.d4).
//   One chain feeds both the number and the fill:
//     v1    = sign(mean) · round(|mean| · 10) / 10   (round ONCE, before both)
//     cells = sign(v1)   · round(|v1|)               (outward from zero)
//   Bar is ALWAYS seven positions, fixed order −3…+3; geometry never depends on the
//   value; position 0 is a 3px hairline, never fillable.
//     · cells > 0 → fill positive cells 1…cells      (amber)
//     · cells < 0 → fill |cells| negative cells       (ember)
//     · |v1| < 0.5 → nothing filled, number still reads +0.4 / −0.3, and the zero
//       cell carries the zm-near MARK (6×6 square, 5×5 dense) in place of the hairline
//     · v1 = 0 (incl. −0) → nothing filled, number reads "0" muted
//     · mean null/undefined (no touched layers) → seven empty cells, number dropped,
//       data-rating-empty. The step-3 gate (FLOOR=2) makes that unreachable from the
//       wizard; it is the honest fallback, not a state of the page.
//   Math.round is not symmetric (Math.round(-1.5) = -1), so sign·round(abs) in both
//   steps; rounding ONCE means the number and the bar never disagree by more than
//   half a cell. No half cells. -------------------------------------------------
const RAT_POS = [-3, -2, -1, 0, 1, 2, 3];
const r1 = (v) => Math.sign(v) * Math.round(Math.abs(v) * 10) / 10;
const ratCells = (v1) => Math.sign(v1) * Math.round(Math.abs(v1));
const ratTxt = (v1) => (Number.isInteger(v1) ? String(Math.abs(v1) === 0 ? 0 : v1) : v1.toFixed(1));
function RatingBar({ mean }) {
  const empty = mean === null || mean === undefined;
  const v1 = empty ? null : r1(mean);
  const n = empty ? 0 : ratCells(v1);
  const num = empty ? '' : `${v1 > 0 ? '+' : ''}${ratTxt(v1)}`;
  const barAttrs = empty
    ? { 'data-rating-empty': 'true' }
    : {
        'data-rating-value': num,
        'data-rating-filled': Math.abs(n),
        'data-rating-mark': Math.abs(v1) < 0.5 ? 'near' : 'none',
        'data-rating-mean': Number(mean.toFixed(4)),
      };
  return (
    <span className="st-rating" data-field="stages[i].rating_bar" data-rating-map="-3..+3 · 7 positions · zero=hairline · cells=sign·round(|v1|)" {...barAttrs}>
      {RAT_POS.map((c) => {
        const fill = !empty && ((n > 0 && c > 0 && c <= n) || (n < 0 && c < 0 && c >= n));
        const zm = c === 0 && !empty && Math.abs(v1) < 0.5 ? ' zm-near' : '';
        // exact canon class string — interior blanks kept for byte-parity
        const cls = `st-rat-cell ${fill ? 'st-rat-fill' : ''} ${c === 0 ? 'st-rat-zero' : ''}${zm} ${c > 0 ? 'st-rat-pos' : 'st-rat-neg'}`;
        return <span key={c} className={cls} data-pos={`${c > 0 ? '+' : ''}${c}`} />;
      })}
      {!empty && (
        <span className={`st-rating-val${v1 < 0 ? ' neg' : ''}${v1 === 0 ? ' zero' : ''}`} data-field="stages[i].rating_numeric">
          {num}
        </span>
      )}
    </span>
  );
}

// ---- one stage card (verbatim `card`) ----
function Card({ s, i, view }) {
  const onRedLine = view.carriers.includes(i);
  const nm = cap(s.name, CAP_NAME);
  const lv = s.bg3 === null || s.bg3 === undefined ? null : cap(s.bg3, CAP_LIVING);

  const dates = s.datesMissing ? null : (
    <div className="st-card-dates" data-field="stages[i].date_range">
      <span className={s.openStart ? 'st-card-open' : undefined}>{s.openStart ? '(open)' : s.start}</span>
      <span className="st-card-arrow">—</span>
      <span>{s.current ? 'present' : s.end}</span>
      {s.dur && <span className="st-card-dur" data-field="stages[i].duration">· {s.dur}</span>}
    </div>
  );

  const liCls = `st-card${s.current ? ' st-card-now' : ''}${onRedLine ? ' st-card-thread' : ''}`;
  return (
    <li className={liCls} {...(s.degradation ? { 'data-degradation': s.degradation } : {})}>
      <div className="st-card-corner">
        <span className="st-card-roman" data-field="stages[i].roman_numeral">{ROMAN(i + 1)}</span>
        {s.current && <span className="st-card-now-flag" data-field="stages[i].now_tag">now</span>}
      </div>
      <div className="st-card-head">
        <h3 className="st-card-name" data-field="stages[i].name" {...(nm.cut ? { 'data-cap-applied': `name:${CAP_NAME}` } : {})}>
          <em>{nm.t}</em>
        </h3>
        {dates}
      </div>
      <div className="st-card-meter">
        <div className="st-card-rating-lbl">rating</div>
        <RatingBar mean={s.layersNone ? null : view.means[i]} />
        {/* мини-кривая читает форму через filled() (s836.d3), как §I/§VII: пустой этап = null,
            интерп даёт форму, не 0-line-ложь. view.means остаётся сырым (число роняет RatingBar).
            Недостижимо за полом деки s3 (≥2 тронутых), null-free борды → filled() тождественна. */}
        <div className="st-card-curve-wrap"><CardCurve means={filled(view.means)} idx={i} /></div>
      </div>
      <dl className="st-card-bs">
        {/* BG1 — мульти-выбор до трёх меток. Разделитель провизорный (BG1_SEP). */}
        {s.bg1 && (
          <div className="st-card-b">
            <dt>top trigger</dt>
            <dd data-field="stages[i].top_trigger" data-bg1-count={s.bg1.length} data-provisional="bg1_separator:· ">
              <em>
                {s.bg1.map((x, k) => (
                  <Fragment key={k}>{k > 0 ? BG1_SEP : ''}<span data-bg1-item="">{x}</span></Fragment>
                ))}
              </em>
            </dd>
          </div>
        )}
        {s.bg2 && (
          <div className="st-card-b"><dt>top fuel</dt><dd data-field="stages[i].top_fuel"><em>{s.bg2}</em></dd></div>
        )}
        {lv && (
          <div className="st-card-b">
            <dt>living for</dt>
            <dd data-field="stages[i].living_for" {...(lv.cut ? { 'data-cap-applied': `living_for:${CAP_LIVING}` } : {})}><em>{lv.t}</em></dd>
          </div>
        )}
        <div className="st-card-b">
          <dt>what's left</dt>
          {s.current ? (
            <dd className="st-card-b-pending" data-field="stages[i].what_left">— still in motion</dd>
          ) : s.bg4 ? (
            <dd data-field="stages[i].what_left"><em>{s.bg4}</em></dd>
          ) : null /* canon omits the dd entirely when not current and no bg4 */}
        </div>
      </dl>
      {onRedLine && (
        <div className="st-card-thread-tag" data-field="stages[i].red_line_marker">
          <span className="st-card-thread-mark">✦</span>
          <em>the red line runs through this one.</em>
          <span className="st-card-thread-ref">→ §iv</span>
        </div>
      )}
    </li>
  );
}

// red-line phrase for the intro — M carriers (verbatim `redLinePhrase`)
function RedLinePhrase({ m }) {
  if (m === 0) return <>no single stage carries the red line on its own — the reading is in how they move together.</>;
  if (m === 1) return <>the red line runs through one of them <em>(see §iv)</em> — the rest are setting.</>;
  return <>the red line runs through {NUM_WORD[m] || m} of them <em>(see §iv)</em> — the rest are setting.</>;
}

// chunk the reverse-chronological order by a per-sheet plan (verbatim `pages`)
function sheetsOf(view, plan) {
  const order = view.stages.map((s, i) => ({ s, i })).reverse();
  const sheets = [];
  const rest = order.slice();
  let pi = 0;
  while (rest.length) sheets.push(rest.splice(0, plan[pi++] || rest.length));
  return sheets;
}

export default function SectionII({ view }) {
  const N = view.stages.length;
  const M = view.carriers.length;
  const dense = N >= 8; // §II density threshold — N≥8 exactly
  const rootRef = useRef(null);

  // Pagination plan. Initial = the board's declared plan (or a single sheet). In the
  // browser the plan is RE-MEASURED to a fixed point (see below); in SSR/jsdom the
  // measurement is inert (rects read 0), so the declared plan is what renders.
  const [plan, setPlan] = useState(view.plan || [N]);

  // ---- measured pagination (s368.d2) — budget is MEASURED, not constant ----------
  // Whatever fits WHOLE on the sheet stays; the remainder goes to the next §II sheet.
  // Cards keep their R9 size (never cut, never compressed). Budget per sheet = artboard
  // height − .st-sheet paddings − header − intro (first sheet) − footnote (last sheet).
  // Re-run to a fixed point: the plan changes intro/footnote heights, which change the
  // plan. Orphan rule (F1): the last sheet is never left with a single card.
  useIsoLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // The algorithm and the measurement live in `docFit.js` since the static document roads
    // (export, the step-7 window) paginate with the SAME functions (s969). Inert under jsdom /
    // before layout (`measureSectionII` → null) — the declared plan stays.
    const r = measureSectionII(root);
    if (!r) return;
    const next = planSheets(r.hs, r.m, r.dense);
    if (next && next.join() !== plan.join()) setPlan(next);
  });

  const sheets = sheetsOf(view, plan);
  const nSheets = sheets.length;

  return (
    <div ref={rootRef} data-section="II">
      {sheets.map((chunk, si) => {
        const first = si === 0;
        const last = si === nSheets - 1;
        const from = ROMAN(chunk[chunk.length - 1].i + 1);
        const to = ROMAN(chunk[0].i + 1);
        return (
          <section
            key={si}
            className={`artboard stages-page r4 r4-stages${dense ? ' st-dense' : ''}`}
            data-screen-label={`${view.screen}${nSheets > 1 ? ` · sheet ${si + 1}/${nSheets}` : ''}`}
            data-run={view.source}
            data-canon="R10-§II-F4"
            data-density={dense ? 'dense' : 'base'}
            data-stage-count={N}
            data-sheet={`${si + 1}/${nSheets}`}
          >
            <div className="ab-paper" />
            <div className="ab-grain" />
            <header className="ab-runner">
              <span data-field="section_header_left"><span className="runner-roman">II</span>stages</span>
              <span className="runner-meta" data-field="section_header_right">life map · the reading</span>
            </header>
            <div className="st-sheet">
              <div className="st-hd st-hd-oneline">
                <div className="st-roman">II</div>
                <h2 className="st-title" data-field="page_title">
                  <em>{NUM_WORD[N] || N} stages.</em>
                  <span className="st-em"> {first ? 'the recorded arc.' : 'the recorded arc, continued.'}</span>
                </h2>
                <div className="st-tag">
                  <span data-field="top_meta_left">{N} stages{view.years ? ` · ${view.years}` : ''}</span>
                  {first ? (
                    <em data-field="top_meta_right">each one is one decision you made.</em>
                  ) : (
                    <em className="st-cont-note" data-field="sheet_continuation">cards {to.toLowerCase()} — {from.toLowerCase()}</em>
                  )}
                  {nSheets > 1 && <span className="st-sheet-chip" data-field="sheet_indicator">sheet {si + 1}/{nSheets}</span>}
                </div>
              </div>
              {first && (
                <p className="st-sub" data-field="intro_paragraph">
                  this is the inventory before the reading. {NUM_WORD[N] || N} stages you named, dated, rated.{' '}
                  <RedLinePhrase m={M} /> each card holds what pulled you in, what you ran on, who you were living
                  for, and what stayed when it ended.
                </p>
              )}
              <ol className="st-cards">
                {chunk.map((o) => <Card key={o.i} s={o.s} i={o.i} view={view} />)}
              </ol>
              {last && (
                <p className="st-foot-note" data-field="how_to_read_it">
                  <span className="st-foot-k">how to read it</span>
                  <em>each card is one stage as you described it. nothing on this page is interpretation — only what you wrote and what you picked. interpretation begins on §iii.</em>
                </p>
              )}
            </div>
            <footer className="ab-foot">
              <span data-field="footer_left">
                <span className="foot-em">life map</span> <span className="foot-faint">·</span> stages <span className="foot-faint">·</span> <span className="foot-amber">{N} of {N}</span>
              </span>
              <span className="foot-page" data-field="footer_right">ii · inventory</span>
            </footer>
          </section>
        );
      })}
    </div>
  );
}
